/*
 * Novelcor landing behaviour. No dependencies.
 *
 * - <nv-scene>: writes scroll progress to --p (0-1) and, with data-segments,
 *   marks children [data-seg-item="i"] as is-active / is-past.
 * - [data-nv-reveal]: adds .is-in once the element enters the viewport.
 * - <nv-compare>: before/after slider (pointer, touch and keyboard).
 * - a[href="#comprar"]: scrolls to the product buy zone.
 *
 * Motion only runs when .nv-motion is on <html>, which is skipped for
 * prefers-reduced-motion. Content is visible and static without it.
 * Every section may include this file; it initialises once.
 */
(() => {
  if (window.NvLanding) return;

  const root = document.documentElement;
  const reduceQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

  const setMotion = () => root.classList.toggle('nv-motion', !reduceQuery.matches);
  setMotion();
  reduceQuery.addEventListener('change', () => {
    setMotion();
    scenes.forEach((scene) => scene.update(true));
  });

  /* ---------------------------------------------------------------------
   * Scroll loop: one rAF per frame for all visible scenes.
   * ------------------------------------------------------------------ */
  const scenes = new Set();
  const visible = new Set();
  let frame = 0;

  const tick = () => {
    frame = 0;
    visible.forEach((scene) => scene.update());
  };
  const requestTick = () => {
    if (!frame && visible.size) frame = requestAnimationFrame(tick);
  };

  window.addEventListener('scroll', requestTick, { passive: true });
  window.addEventListener('resize', requestTick, { passive: true });

  const sceneObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) visible.add(entry.target);
        else visible.delete(entry.target);
        entry.target.update(true);
      });
    },
    { rootMargin: '25% 0px' }
  );

  class NvScene extends HTMLElement {
    connectedCallback() {
      this.progress = -1;
      this.segment = -1;
      this.segments = parseInt(this.dataset.segments || '0', 10);
      this.items = Array.from(this.querySelectorAll('[data-seg-item]'));
      scenes.add(this);
      sceneObserver.observe(this);
      if (this.dataset.segMode === 'observe') {
        this.observeTriggers();
      } else if (this.segments > 0 && !this.hasAttribute('data-sticky')) {
        // No scroll scene: segments change only from controls.
        this.manual = true;
        this.setSegment(0);
      }
      this.update(true);
    }

    // Scroll-spy: the active segment is the last [data-seg-trigger] whose top
    // has passed a reading line. When the list is stacked under a sticky photo
    // ([data-seg-anchor] above the text) the line sits just below the photo;
    // otherwise it is the middle of the viewport. Works with or without motion.
    observeTriggers() {
      this.manual = true;
      this.triggers = Array.from(this.querySelectorAll('[data-seg-trigger]'));
      this.anchor = this.querySelector('[data-seg-anchor]');
      this.setSegment(0);
    }

    spy() {
      if (!this.triggers || !this.triggers.length) return;
      const viewport = window.innerHeight;
      let line = viewport * 0.5;
      if (this.anchor) {
        const box = this.anchor.getBoundingClientRect();
        const stacked = box.bottom < viewport * 0.7 && box.width > this.getBoundingClientRect().width * 0.6;
        if (stacked) line = box.bottom + (viewport - box.bottom) * 0.3;
      }
      let index = 0;
      this.triggers.forEach((trigger, i) => {
        if (trigger.getBoundingClientRect().top <= line) index = i;
      });
      const last = this.triggers[this.triggers.length - 1].getBoundingClientRect();
      if (last.bottom <= viewport - 8) index = this.triggers.length - 1;
      this.setSegment(parseInt(this.triggers[index].dataset.segTrigger, 10));
    }

    disconnectedCallback() {
      scenes.delete(this);
      visible.delete(this);
      sceneObserver.unobserve(this);
    }

    get isSticky() {
      return this.hasAttribute('data-sticky') && root.classList.contains('nv-motion');
    }

    measure() {
      const rect = this.getBoundingClientRect();
      const viewport = window.innerHeight;
      if (this.isSticky) {
        const travel = rect.height - viewport;
        return travel > 0 ? -rect.top / travel : 0;
      }
      // Non-sticky scenes: 0 when the top enters, 1 when the bottom leaves.
      return (viewport - rect.top) / (viewport + rect.height);
    }

    update(force = false) {
      if (this.triggers) this.spy();
      if (!root.classList.contains('nv-motion')) {
        if (force) this.applyStatic();
        return;
      }
      const progress = clamp(this.measure(), 0, 1);
      if (!force && Math.abs(progress - this.progress) < 0.0008) return;
      this.progress = progress;
      this.style.setProperty('--p', progress.toFixed(4));
      if (this.segments > 0 && !this.manual) {
        this.setSegment(Math.min(this.segments - 1, Math.floor(progress * this.segments)));
      }
    }

    applyStatic() {
      this.style.removeProperty('--p');
      if (this.segments > 0 && this.segment < 0 && !this.manual) this.setSegment(0);
    }

    setSegment(index) {
      if (index === this.segment) return;
      this.segment = index;
      this.dataset.seg = String(index);
      this.style.setProperty('--seg', index);
      this.toggleAttribute('data-last', index === this.segments - 1);
      const origin = this.items.find((item) => item.dataset.segOrigin && parseInt(item.dataset.segItem, 10) === index);
      if (origin) this.style.setProperty('--seg-origin', origin.dataset.segOrigin);
      this.items.forEach((item) => {
        const own = parseInt(item.dataset.segItem, 10);
        item.classList.toggle('is-active', own === index);
        item.classList.toggle('is-past', own < index);
        if (item.matches('[aria-pressed]')) item.setAttribute('aria-pressed', String(own === index));
      });
      this.dispatchEvent(new CustomEvent('nv:segment', { detail: { index } }));
    }

    // Jump to a segment from a control. With motion, scroll the page so the
    // scene's own progress lands there; otherwise switch directly.
    goTo(index) {
      const trigger = this.querySelector(`[data-seg-trigger="${index}"]`);
      if (trigger) {
        trigger.scrollIntoView({ behavior: reduceQuery.matches ? 'auto' : 'smooth', block: 'center' });
      } else if (this.isSticky) {
        const rect = this.getBoundingClientRect();
        const travel = rect.height - window.innerHeight;
        const target = window.scrollY + rect.top + travel * ((index + 0.5) / this.segments);
        window.scrollTo({ top: target, behavior: 'smooth' });
      } else {
        this.setSegment(index);
      }
    }
  }

  if (!customElements.get('nv-scene')) customElements.define('nv-scene', NvScene);

  // Controls inside a scene: <button data-nv-goto="1">
  document.addEventListener('click', (event) => {
    const control = event.target.closest('[data-nv-goto]');
    if (!control) return;
    const scene = control.closest('nv-scene');
    if (scene) scene.goTo(parseInt(control.dataset.nvGoto, 10));
  });

  /* ---------------------------------------------------------------------
   * Reveal on enter
   * ------------------------------------------------------------------ */
  const revealObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-in');
        revealObserver.unobserve(entry.target);
      });
    },
    { rootMargin: '0px 0px -12% 0px' }
  );

  const observeReveals = (scope = document) => {
    scope.querySelectorAll('[data-nv-reveal]:not(.is-in)').forEach((el) => revealObserver.observe(el));
  };

  /* ---------------------------------------------------------------------
   * Before / after comparator
   * ------------------------------------------------------------------ */
  class NvCompare extends HTMLElement {
    connectedCallback() {
      this.frame = this.querySelector('[data-compare-frame]');
      this.input = this.querySelector('input[type="range"]');
      if (!this.frame || !this.input) return;

      this.set(parseFloat(this.input.value));
      this.input.addEventListener('input', () => this.set(parseFloat(this.input.value)));

      this.onDown = (event) => {
        if (event.button !== undefined && event.button !== 0) return;
        this.dragging = true;
        this.startX = event.clientX;
        this.startY = event.clientY;
        this.decided = event.pointerType === 'mouse';
        if (this.decided) this.fromPointer(event);
      };
      this.onMove = (event) => {
        if (!this.dragging) return;
        if (!this.decided) {
          const dx = Math.abs(event.clientX - this.startX);
          const dy = Math.abs(event.clientY - this.startY);
          if (dx < 6 && dy < 6) return;
          // A mostly vertical gesture is a page scroll: let it go.
          if (dy > dx) {
            this.dragging = false;
            return;
          }
          this.decided = true;
          this.frame.setPointerCapture?.(event.pointerId);
        }
        this.fromPointer(event);
      };
      this.onUp = (event) => {
        if (this.dragging && !this.decided && event.type === 'pointerup') this.fromPointer(event);
        this.dragging = false;
      };

      this.frame.addEventListener('pointerdown', this.onDown);
      this.frame.addEventListener('pointermove', this.onMove);
      this.frame.addEventListener('pointerup', this.onUp);
      this.frame.addEventListener('pointercancel', this.onUp);

      if (root.classList.contains('nv-motion') && this.hasAttribute('data-hint')) {
        const hintObserver = new IntersectionObserver(
          ([entry]) => {
            if (!entry.isIntersecting) return;
            hintObserver.disconnect();
            this.hint();
          },
          { threshold: 0.6 }
        );
        hintObserver.observe(this.frame);
      }
    }

    fromPointer(event) {
      const rect = this.frame.getBoundingClientRect();
      const value = ((event.clientX - rect.left) / rect.width) * 100;
      this.set(value);
      this.input.value = String(Math.round(this.position));
    }

    set(value) {
      this.position = clamp(value, 0, 100);
      this.style.setProperty('--pos', `${this.position}%`);
      this.input.setAttribute('aria-valuetext', `${Math.round(this.position)} % antes`);
    }

    // One gentle sweep to show the slider moves; cancelled by any interaction.
    hint() {
      const start = this.position;
      const keys = [start, start - 14, start + 10, start];
      const duration = 1600;
      let t0;
      const cancel = () => (this.hinting = false);
      this.hinting = true;
      this.frame.addEventListener('pointerdown', cancel, { once: true });
      this.input.addEventListener('focus', cancel, { once: true });
      const step = (now) => {
        if (!this.hinting) return;
        t0 ??= now;
        const t = Math.min(1, (now - t0) / duration);
        const seg = Math.min(keys.length - 2, Math.floor(t * (keys.length - 1)));
        const local = t * (keys.length - 1) - seg;
        const eased = 0.5 - Math.cos(local * Math.PI) / 2;
        this.set(keys[seg] + (keys[seg + 1] - keys[seg]) * eased);
        if (t < 1) requestAnimationFrame(step);
        else this.input.value = String(Math.round(this.position));
      };
      requestAnimationFrame(step);
    }
  }

  if (!customElements.get('nv-compare')) customElements.define('nv-compare', NvCompare);

  /* ---------------------------------------------------------------------
   * <nv-carousel>: native scroll-snap track with optional prev/next buttons.
   * Swiping is plain scrolling; the buttons only scroll by one card.
   * ------------------------------------------------------------------ */
  class NvCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-carousel-track]');
      this.prev = this.querySelector('[data-carousel-prev]');
      this.next = this.querySelector('[data-carousel-next]');
      if (!this.track) return;
      const step = (dir) => {
        const card = this.track.firstElementChild;
        const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
        const width = card ? card.getBoundingClientRect().width + gap : this.track.clientWidth;
        this.track.scrollBy({ left: dir * width, behavior: reduceQuery.matches ? 'auto' : 'smooth' });
      };
      this.prev?.addEventListener('click', () => step(-1));
      this.next?.addEventListener('click', () => step(1));
      this.sync = () => {
        const max = this.track.scrollWidth - this.track.clientWidth - 2;
        if (this.prev) this.prev.disabled = this.track.scrollLeft <= 2;
        if (this.next) this.next.disabled = this.track.scrollLeft >= max;
      };
      this.track.addEventListener('scroll', () => requestAnimationFrame(this.sync), { passive: true });
      window.addEventListener('resize', this.sync, { passive: true });
      this.sync();
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this.sync);
    }
  }

  if (!customElements.get('nv-carousel')) customElements.define('nv-carousel', NvCarousel);

  /* ---------------------------------------------------------------------
   * "#comprar" links scroll to the buy zone of the product template.
   * ------------------------------------------------------------------ */
  document.addEventListener('click', (event) => {
    const link = event.target.closest('a[href="#comprar"]');
    if (!link) return;
    const target =
      document.getElementById('comprar') ||
      document.querySelector('[id^="MainProduct-"]') ||
      document.querySelector('product-info');
    if (!target) return;
    event.preventDefault();
    target.scrollIntoView({ behavior: reduceQuery.matches ? 'auto' : 'smooth', block: 'start' });
  });

  /* ---------------------------------------------------------------------
   * Boot + theme editor
   * ------------------------------------------------------------------ */
  // Space above the hero (announcement bar + header), so the first viewport
  // fits the whole hero including its CTA.
  const measureChrome = () => {
    const hero = document.querySelector('.nv-hero, .nv-reveal');
    if (!hero) return;
    const offset = hero.getBoundingClientRect().top + window.scrollY;
    root.style.setProperty('--nv-header-h', `${Math.round(clamp(offset, 0, 200))}px`);
  };

  const boot = (scope) => {
    measureChrome();
    observeReveals(scope);
    requestTick();
  };

  window.addEventListener('resize', () => requestAnimationFrame(measureChrome), { passive: true });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => boot());
  else boot();

  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', (event) => {
      event.target.querySelectorAll('[data-nv-reveal]').forEach((el) => el.classList.add('is-in'));
      boot(event.target);
    });
    document.addEventListener('shopify:block:select', (event) => {
      const block = event.target;
      const scene = block.closest('nv-scene');
      const index = block.dataset.segItem;
      if (scene && index !== undefined) scene.goTo(parseInt(index, 10));
    });
  }

  window.NvLanding = { scenes };
})();
